import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { Check } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  formatLimit,
  formatRetention,
  LIMIT_GRACE_DAYS,
  PLAN_BY_ID,
  planPrice,
  SELF_SERVE_PLANS,
  YEARLY_SAVING_PERCENT,
  FOUNDING_DISCOUNT_PERCENT,
  FOUNDING_NOTICE_DAYS,
  type BillingInterval,
  type SelfServePlanId,
} from "@metrickle/schema/plans";
import { formatMoney, localEstimate } from "@metrickle/schema/currency";
import {
  AI_FEATURE_LABEL,
  clearPlanIntent,
  fmtPeriod,
  openPortal,
  planIntent,
  startCheckout,
  useAiBreakdown,
  useBilling,
  useFx,
  type AiBreakdown,
  type Billing,
  type MeterLine,
} from "../lib/billing";
import { useWorkspace } from "../lib/support";
import { track } from "../lib/track";
import {
  Badge,
  Button,
  Callout,
  Card,
  CardEmpty,
  CardHeader,
  CardSection,
  cx,
  ErrorState,
  Meter,
  PageHeader,
  Segmented,
  SelectField,
  Skeleton,
  Table,
  TableEmptyRow,
  TableScroll,
  notifyError,
} from "../components/ui";
import { GrowthRequest } from "../components/growth-request";
import { usePageTitle } from "../lib/page-title";
import { fmtNumber } from "../lib/format";

const fmtDate = (t: number) => new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

/** Workspace plan, usage and self-serve billing (Stripe Checkout and Customer Portal). */
export function BillingPage() {
  usePageTitle("Plan & billing");
  const workspace = useWorkspace();
  const q = useBilling(workspace.id);
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const returned = params.get("checkout");
  const notice = useRef<HTMLDivElement>(null);

  // Back from Stripe: the webhook usually lands within seconds, so refresh a few times, and move
  // focus to the message so keyboard and screen reader users hear what happened.
  useEffect(() => {
    if (!returned) return;
    notice.current?.focus();
    track("checkout_returned", { result: returned });
    if (returned !== "success") return;
    clearPlanIntent();
    const ids = [2000, 5000, 10000].map((ms) => setTimeout(() => void qc.invalidateQueries({ queryKey: ["billing"] }), ms));
    return () => ids.forEach(clearTimeout);
  }, [returned, qc]);

  const b = q.data;
  return (
    <>
      <PageHeader title="Plan & billing" subtitle={workspace.name} />
      <div className="max-w-5xl space-y-4">
        {returned && (
          // The wrapper takes focus on return from Stripe; Callout doesn't forward a ref.
          <div ref={notice} tabIndex={-1} role="status" className="rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-accent">
            <Callout tone={returned === "success" ? "info" : "neutral"} title={returned === "success" ? "Thank you!" : undefined} onDismiss={() => setParams({}, { replace: true })}>
              {returned === "success"
                ? `${b?.plan.source === "stripe" ? `You're on ${PLAN_BY_ID[b.plan.plan].name}.` : "Your payment went through; your plan updates in a few seconds."} Stripe emails your receipt.`
                : "Checkout was cancelled, so nothing was charged. Your plan hasn't changed."}
            </Callout>
          </div>
        )}
        {q.error ? (
          <Card>
            <ErrorState title="Couldn't load billing" error={q.error} />
          </Card>
        ) : !b ? (
          <Skeleton className="h-64" />
        ) : (
          <BillingBody b={b} orgId={workspace.id!} workspaceName={workspace.name ?? ""} />
        )}
      </div>
    </>
  );
}

function BillingBody({ b, orgId, workspaceName }: { b: Billing; orgId: string; workspaceName: string }) {
  return (
    <>
      <StatusNotice b={b} orgId={orgId} />
      <CurrentPlan b={b} orgId={orgId} />
      {!b.open && b.plan.plan === "free" && b.canManage && <GrowthRequest orgId={orgId} workspaceName={workspaceName} />}
      <Usage b={b} />
      <AiCredits orgId={orgId} />
      <PlanPicker b={b} orgId={orgId} />
      <Card>
        <CardHeader title="Good to know" />
        <CardSection>
          <ul className="list-disc space-y-1 pl-5 text-sm text-ink-2">
            {b.open && <li>Cancel any time; your plan runs to the end of the period you've paid for.</li>}
            <li>
              Nothing is ever deleted when you change plan or cancel. Each plan shows a window of history ({formatRetention(PLAN_BY_ID.free.limits.retentionDays)} on Free); older data is kept
              and shows again on a plan with more. Going over a limit never loses data either: you get {LIMIT_GRACE_DAYS} days' notice first.
            </li>
            <li>AI credits come with every plan. When they run out, features keep working without AI until the month resets, and nothing is charged.</li>
            {b.open && <li>Changed your mind? We refund your first payment in full if you ask within 14 days.</li>}
            {b.open && (
              <li>
                Prices are in pounds sterling (GBP). Outside the UK, checkout shows and charges your own currency at the day's rate, so you see the exact amount before paying. Any VAT or
                sales tax that applies is added at checkout, and you can add a tax ID for your invoices.
              </li>
            )}
          </ul>
        </CardSection>
      </Card>
    </>
  );
}

/** The one thing that needs attention, if any: a failed payment, a trial ending, a limit. */
function StatusNotice({ b, orgId }: { b: Billing; orgId: string }) {
  const p = b.plan;
  const portal = () => openPortal(orgId).catch(notifyError);
  let n: { tone: "neutral" | "warn" | "bad"; title: string; body: React.ReactNode; action?: React.ReactNode } | null = null;
  if (p.source === "early_access") {
    n = {
      tone: "neutral",
      title: "You're in early access: Growth is free while we launch.",
      body: `When paid plans start you'll get ${FOUNDING_NOTICE_DAYS} days' notice, then ${FOUNDING_DISCOUNT_PERCENT}% off any plan for as long as you stay, as a founding customer. Nothing to do until then.`,
    };
  } else if (p.status === "past_due") {
    n = {
      tone: "bad",
      title: "Your last payment didn't go through.",
      body: "Everything keeps working while you update your card; we'll retry automatically.",
      action: b.canManage && b.hasCustomer && (
        <Button size="sm" variant="primary" onClick={portal}>
          Update payment details
        </Button>
      ),
    };
  } else if (b.level === "over") {
    n = {
      tone: b.locked ? "bad" : "warn",
      title: "Over your plan's limits.",
      body: b.locked
        ? `New replays are paused${b.sampleRate < 1 ? " and events are being sampled" : ""} until you ${b.open ? "upgrade" : "get Growth"} or usage resets on ${fmtDate(b.resetsAt)}.`
        : `Nothing is limited yet. From ${fmtDate(b.graceEndsAt!)} new replays pause until you ${b.open ? "upgrade" : "get Growth"} or usage resets on ${fmtDate(b.resetsAt)}.`,
    };
  } else if (p.trialDaysLeft !== null) {
    n = {
      tone: p.trialDaysLeft <= 3 ? "warn" : "neutral",
      title: p.trialDaysLeft === 0 ? "Your Growth trial ends today." : `${p.trialDaysLeft} ${p.trialDaysLeft === 1 ? "day" : "days"} left in your Growth trial.`,
      body: `Pick a plan to keep replay, heatmaps, studies and every protected task${p.founding ? `, with your founding ${FOUNDING_DISCOUNT_PERCENT}% off for good` : ""}. Otherwise you'll move to Free on ${fmtDate(p.trialEndsAt!)}, and nothing is deleted.`,
    };
  } else if (p.cancelAtPeriodEnd && p.currentPeriodEnd) {
    n = {
      tone: "warn",
      title: `Your plan ends on ${fmtDate(p.currentPeriodEnd)}.`,
      body: "You won't be charged again, and you'll move to Free.",
      action: b.canManage && b.hasCustomer && (
        <Button size="sm" onClick={portal}>
          Keep my plan
        </Button>
      ),
    };
  }
  if (!n) return null;
  return (
    <Callout role="status" tone={n.tone} title={n.title} action={n.action || undefined}>
      {n.body}
    </Callout>
  );
}

function CurrentPlan({ b, orgId }: { b: Billing; orgId: string }) {
  const p = b.plan;
  const plan = PLAN_BY_ID[p.plan];
  const [busy, setBusy] = useState(false);
  const manage = async () => {
    setBusy(true);
    await openPortal(orgId).catch((e) => {
      setBusy(false);
      notifyError(e);
    });
  };
  const paying = p.source === "stripe" && p.status !== "canceled";
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            Current plan <Badge tone="accent">{plan.name}</Badge>
            {p.trialDaysLeft !== null && <Badge>Trial</Badge>}
            {p.source === "early_access" && <Badge tone="good">Early access</Badge>}
            {p.founding && <Badge tone="good">Founding customer</Badge>}
            {p.status === "past_due" && <Badge tone="bad">Payment due</Badge>}
          </span>
        }
        subtitle={plan.audience}
        actions={
          b.canManage && b.hasCustomer && b.billingEnabled ? (
            <Button size="sm" onClick={manage} pending={busy} pendingLabel="Opening…">
              Manage billing
            </Button>
          ) : null
        }
      />
      <CardSection className="space-y-1 text-sm text-ink-2">
        <p>
          {p.source === "early_access" ? (
            <>Free during early access. No card on file.</>
          ) : p.trialDaysLeft !== null ? (
            <>Free until {fmtDate(p.trialEndsAt!)}. No card on file.</>
          ) : !p.price ? (
            "Your price is agreed with the Metrickle team."
          ) : p.price.chargeCents === 0 ? (
            <>Free for good. Upgrade whenever you need more.</>
          ) : (
            <>
              <strong className="text-ink">
                {formatMoney(p.price.chargeCents)} a {p.interval}
              </strong>
              {p.interval === "year" && <> ({formatMoney(p.price.perMonthCents)} a month)</>}
              {paying && p.currentPeriodEnd && <>, {p.cancelAtPeriodEnd ? "ends" : "renews"} on {fmtDate(p.currentPeriodEnd)}</>}
              {" "}plus any tax.
            </>
          )}
        </p>
        {b.canManage && b.hasCustomer && <p className="text-xs text-ink-3">Change plan, update your card, download invoices or cancel under Manage billing.</p>}
        {b.billingEmail && <p className="text-xs text-ink-3">Receipts go to {b.billingEmail}.</p>}
      </CardSection>
    </Card>
  );
}

const METER_LABEL: Record<MeterLine["metric"], string> = {
  events: "Events this month",
  replays: "Recordings this month",
  apps: "Apps",
  surveys: "Live surveys",
  contracts: "Protected tasks",
  ai: "AI credits this month",
};

function Usage({ b }: { b: Billing }) {
  return (
    <Card>
      <CardHeader title="Usage" subtitle={`Monthly counts start again on ${fmtDate(b.resetsAt)}.`} />
      <CardSection>
        <ul className="grid gap-4 @xl:grid-cols-2">
          {b.meters.map((m) => {
            const unlimited = m.limit === null;
            const none = m.limit === 0;
            const pct = unlimited || none ? 0 : Math.round(m.ratio * 100);
            const over = m.limit !== null && m.used > m.limit;
            const near = !over && (m.metric === "events" || m.metric === "replays" || m.metric === "ai") && m.ratio >= 0.8;
            const aiOut = m.metric === "ai" && m.limit !== null && m.used >= m.limit;
            const value = none
              ? "Not on this plan"
              : unlimited
                ? `${fmtNumber(m.used, { exact: true })} · ${b.plan.plan === "enterprise" ? "custom" : "unlimited"}`
                : m.metric === "ai"
                  ? `${fmtNumber(m.used, { exact: true })} of ${fmtNumber(m.limit!, { exact: true })} · ${aiOut ? "none left" : `${fmtNumber(m.limit! - m.used, { exact: true })} left`}`
                  : `${fmtNumber(m.used, { exact: true })} of ${fmtNumber(m.limit!, { exact: true })}${over ? " · over" : ""}`;
            return (
              <li key={m.metric}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="text-ink-2">{METER_LABEL[m.metric]}</span>
                  <span className={cx("tabular", over && m.metric !== "ai" ? "font-semibold text-bad" : aiOut ? "font-semibold text-ink" : "text-ink")}>{value}</span>
                </div>
                {!unlimited && !none && (
                  <Meter
                    className="mt-1"
                    value={m.used}
                    max={m.limit!}
                    tone={m.metric === "ai" ? (aiOut || near ? "warn" : "accent") : over ? "bad" : near ? "warn" : "accent"}
                    label={METER_LABEL[m.metric]}
                    valueText={
                      m.metric === "ai"
                        ? `${fmtNumber(m.used, { exact: true })} of ${fmtNumber(m.limit!, { exact: true })} used, ${aiOut ? "none" : fmtNumber(m.limit! - m.used, { exact: true })} left`
                        : `${fmtNumber(m.used, { exact: true })} of ${fmtNumber(m.limit!, { exact: true })}, ${pct}%${over ? ", over the limit" : ""}`
                    }
                  />
                )}
                {m.metric === "ai" && (
                  <p className="mt-1 text-xs text-ink-2">
                    {aiOut
                      ? `Used up for this month. Release causes still rank the changes and tickets are sorted by keywords, without AI, until ${fmtDate(b.resetsAt)}. Nothing extra is charged.`
                      : "A credit is about 1p of AI: sorting a support ticket costs 1, reading a release's changes about 10. When they run out, features keep working without AI."}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </CardSection>
    </Card>
  );
}

const plural = (n: number, one: string, many: string) => `${fmtNumber(n, { exact: true })} ${n === 1 ? one : many}`;
const AI_FEATURE_UNIT: Record<AiBreakdown["features"][number]["feature"], [string, string]> = {
  release_cause: ["reading", "readings"],
  ticket_sorting: ["ticket", "tickets"],
};

/** Where the month's AI credits went: by feature, by app, and (for owners and admins) by person. */
function AiCredits({ orgId }: { orgId: string }) {
  const [period, setPeriod] = useState<string>();
  const q = useAiBreakdown(orgId, period);
  const d = q.data;
  const month = d ? fmtPeriod(d.period) : null;
  return (
    <Card>
      <CardHeader
        title="Where AI credits went"
        subtitle={month ? `${month}, by feature and by who asked` : "By feature and by who asked"}
        actions={
          d && d.periods.length > 1 ? (
            <SelectField aria-label="Month" items={d.periods.map((p) => ({ value: p, label: fmtPeriod(p) }))} value={d.period} onChange={setPeriod} />
          ) : null
        }
      />
      {q.error ? (
        <ErrorState title="Couldn't load AI credits" error={q.error} />
      ) : !d ? (
        <Skeleton className="m-4 h-24" />
      ) : d.used === 0 ? (
        <CardEmpty divided>No AI credits used in {month}.</CardEmpty>
      ) : (
        <div aria-busy={q.isFetching || undefined}>
          <CardSection>
            <ul className="grid gap-2 text-sm @xl:grid-cols-2">
              {d.features.map((f) => (
                <li key={f.feature} className="flex items-baseline justify-between gap-2 rounded-lg bg-surface-2 px-3 py-2">
                  <span className="text-ink-2">
                    {AI_FEATURE_LABEL[f.feature]} <span className="text-xs text-ink-3">· {plural(f.calls, ...AI_FEATURE_UNIT[f.feature])}</span>
                  </span>
                  <span className="font-medium text-ink tabular">{plural(f.credits, "credit", "credits")}</span>
                </li>
              ))}
            </ul>
            {d.untracked > 0 && (
              <p className="mt-2 text-xs text-ink-3">
                {plural(d.untracked, "credit was", "credits were")} used before Metrickle recorded what they were for, so they aren't in the breakdown.
              </p>
            )}
          </CardSection>
          {d.people ? <AiPeople d={d} /> : <CardSection className="text-xs text-ink-3">Owners and admins can see who used them.</CardSection>}
          {d.apps.length > 1 && (
            <TableScroll label="AI credits by app" divided>
              <Table caption={`AI credits used in ${month} by app, most first`}>
                <thead>
                  <tr>
                    <th scope="col">App</th>
                    <th scope="col" className="text-right">Credits</th>
                  </tr>
                </thead>
                <tbody>
                  {d.apps.map((a) => (
                    <tr key={a.appId}>
                      <th scope="row" className="font-medium text-ink">{a.name}</th>
                      <td className="text-right font-medium text-ink">{fmtNumber(a.credits, { exact: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
        </div>
      )}
    </Card>
  );
}

function AiPeople({ d }: { d: AiBreakdown }) {
  const people = d.people!;
  return (
    <>
      <TableScroll label="AI credits by person" divided>
        <Table caption={`AI credits used in ${fmtPeriod(d.period)} by the person who asked, most first`} className="min-w-[520px]">
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col" className="text-right">Readings</th>
              <th scope="col" className="text-right">Through an assistant</th>
              <th scope="col" className="text-right">Credits</th>
            </tr>
          </thead>
          <tbody>
            {people.length === 0 ? (
              <TableEmptyRow columns={4}>Nobody asked for AI in {fmtPeriod(d.period)}. It all went on work that runs on its own.</TableEmptyRow>
            ) : (
              people.map((p) => (
                <tr key={p.userId}>
                  <th scope="row">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-ink">{p.name}</span>
                      {!p.member && <Badge>Left the workspace</Badge>}
                    </span>
                    {p.email && p.email !== p.name && <span className="block text-xs text-ink-3">{p.email}</span>}
                  </th>
                  <td className="text-right text-ink-2">{fmtNumber(p.calls, { exact: true })}</td>
                  <td className="text-right text-ink-2">{fmtNumber(p.assistantCredits, { exact: true })}</td>
                  <td className="text-right font-medium text-ink">{fmtNumber(p.credits, { exact: true })}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableScroll>
      <CardSection className="text-xs text-ink-3">
        “Through an assistant” counts credits used by the person’s AI assistant with their API token.
        {d.automatic > 0 && <> {plural(d.automatic, "credit", "credits")} went on ticket sorting, which runs on its own as tickets are imported.</>}
      </CardSection>
    </>
  );
}

function PlanPicker({ b, orgId }: { b: Billing; orgId: string }) {
  const intent = planIntent();
  const [interval, setInterval] = useState<BillingInterval>(intent?.interval ?? (b.plan.source === "stripe" ? b.plan.interval : "year"));
  const [pending, setPending] = useState<SelfServePlanId | null>(null);
  const fx = useFx().data ?? null;
  const p = b.plan;
  if (p.source === "manual" && (p.storedPlan === "enterprise" || p.customMonthly !== null)) return null;
  const paying = p.source === "stripe" && p.status !== "canceled";

  const choose = async (plan: SelfServePlanId) => {
    setPending(plan);
    track("checkout_started", { plan, interval });
    try {
      await startCheckout(orgId, plan, interval);
    } catch (e) {
      setPending(null);
      notifyError(e);
    }
  };

  return (
    <Card>
      <CardHeader
        title={!b.open ? "Plans after early access" : paying ? "Change plan" : "Choose a plan"}
        subtitle={
          !b.open
            ? p.founding
              ? `What each plan will cost once paid plans start. As a founding customer you'll get ${FOUNDING_DISCOUNT_PERCENT}% off these.`
              : `What each plan will cost once paid plans start. Get Growth during early access and you'll get ${FOUNDING_DISCOUNT_PERCENT}% off these, as a founding customer.`
            : "Every plan includes every accessibility, browser, operating system and device comparison, funnels, journeys and AI assistants over MCP."
        }
        actions={
          <Segmented
            aria-label="Billing period"
            value={interval}
            onChange={(v) => setInterval(v as BillingInterval)}
            items={[
              { value: "month", label: "Monthly" },
              { value: "year", label: `Yearly · save ${YEARLY_SAVING_PERCENT}%` },
            ]}
          />
        }
      />
      <CardSection>
        <ul className="grid gap-3 @3xl:grid-cols-3">
          {SELF_SERVE_PLANS.map((id) => {
            const plan = PLAN_BY_ID[id];
            const price = planPrice({ plan: id, interval })!;
            const current = paying && p.plan === id && p.interval === interval;
            const highlighted = intent ? intent.plan === id : plan.popular;
            return (
              <li key={id} className={cx("flex flex-col rounded-lg border p-4", highlighted ? "border-accent" : "border-line")}>
                <h3 className="flex items-center gap-2 font-semibold text-ink">
                  {plan.name}
                  {plan.popular && <Badge tone="accent">Most popular</Badge>}
                </h3>
                <p className="text-xs text-ink-2">{plan.audience}</p>
                <p className="mt-3 text-ink">
                  <span className="text-2xl font-semibold tabular">{formatMoney(price.perMonthCents)}</span>
                  <span className="text-sm text-ink-2"> a month</span>
                </p>
                <p className="text-xs text-ink-3">{interval === "year" ? `${formatMoney(price.chargeCents)} billed yearly` : "Billed monthly"}</p>
                {fx && <p className="text-xs text-ink-3">{`${localEstimate(price.perMonthCents, fx).replace(/^about/, "About")} a month, charged in your currency at checkout`}</p>}
                <ul className="mt-3 flex-1 space-y-1 text-sm text-ink-2">
                  {[
                    `${formatLimit(plan.limits.events)} events a month`,
                    `${formatLimit(plan.limits.replays)} recordings a month`,
                    `${formatLimit(plan.limits.aiCredits)} AI credits a month`,
                    plan.limits.apps === null ? "Unlimited apps" : `${plan.limits.apps} apps`,
                    `${formatRetention(plan.limits.retentionDays)} of history`,
                    // The limits above already cover history and app counts.
                    ...plan.features.filter((f) => !/history|unlimited apps/i.test(f)).slice(0, 3),
                  ].map((f) => (
                    <li key={f} className="flex gap-1.5">
                      <Check aria-hidden size={14} className="mt-0.5 shrink-0 text-accent-text" />
                      {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  {current ? (
                    <p className="text-center text-sm font-medium text-ink-2">Your current plan</p>
                  ) : !b.open || !b.canManage || !b.billingEnabled ? null : (
                    <Button variant={highlighted ? "primary" : "secondary"} className="w-full" disabled={pending !== null} pending={pending === id} pendingLabel="Opening checkout…" onClick={() => choose(id)}>
                      {paying ? `Switch to ${plan.name}` : `Choose ${plan.name}`}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </CardSection>
      <CardSection className="text-sm text-ink-2">
        {!b.open ? (
          <p>Paid plans aren't open yet, so there's nothing to buy. Questions? Email <a className="text-accent-text underline" href="mailto:chris@metrickle.com">chris@metrickle.com</a>.</p>
        ) : !b.canManage ? (
          <p>Only people who manage billing (owners and admins, unless your workspace changed that) can change the plan. Ask one of them, or see the team on <Link className="text-accent-text underline" to="/workspace">Team & workspace</Link>.</p>
        ) : !b.billingEnabled ? (
          <p>
            Online checkout isn't switched on yet. Email <a className="text-accent-text underline" href="mailto:chris@metrickle.com?subject=Upgrading%20Metrickle">chris@metrickle.com</a> and we'll set up your plan today.
          </p>
        ) : (
          <p>
            Payment is handled securely by Stripe. Need more volume, SCIM or an agreed SLA?{" "}
            <a className="text-accent-text underline" href="mailto:chris@metrickle.com?subject=Metrickle%20Enterprise">Talk to us about Enterprise</a>.
          </p>
        )}
      </CardSection>
    </Card>
  );
}
